import React, { useState, useEffect } from 'react';
import { useCookies } from 'react-cookie';
import { useDispatch } from 'react-redux';
import Button from '../../components/buttons/Button';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import ApiService from '../../services/ApiService';
import ReCAPTCHA from 'react-google-recaptcha';
import { setMerchants, setSelectedMerchant } from '@store/slices/merchantSlice';
import { getMerchantsWithLoginFallback } from '@src/utils/merchantHelpers';
import { getErrorMessage, getErrorStatus } from '@src/utils/errorMessage';
import { setCardDetails } from '@store/slices/authDetails';
import { toast } from 'react-toastify';
import type { Merchant } from '@src/types/merchant';

interface OtpInputPageProps {
  username: string;
  password: string;
  onBackToLogin: () => void;
}

const OtpInputPage: React.FC<OtpInputPageProps> = ({
  username,
  password,
  onBackToLogin,
}) => {
  const dispatch = useDispatch();
  const [otp, setOtp] = useState<string[]>(new Array(6).fill(''));
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState('');
  const [inputError, setInputError] = useState(false);
  const [countdown, setCountdown] = useState(60);
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const [recaptchaVal, setRecaptchaVal] = useState<null | string>(null);
  const navigate = useNavigate();
  const { handleLogin, handleLogout } = useAuth();
  const [, setCookie] = useCookies([
    'authToken',
    'refreshToken',
    'loungeId',
    'loungeDetails',
    'role',
  ]);
  const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;

  useEffect(() => {
    let timer: ReturnType<typeof setInterval>;
    if (countdown > 0 && isResendDisabled) {
      timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    } else {
      setIsResendDisabled(false);
    }

    return () => {
      if (timer) clearInterval(timer);
    };
  }, [countdown, isResendDisabled]);

  const handleResendOtp = async () => {
    try {
      setResendLoading(true);
      await ApiService.post('v1/user/staff/login', {
        username: username,
        password: password,
      });
      setIsResendDisabled(true);
      setCountdown(60);
      setError('');
      setInputError(false);
      setOtp(new Array(6).fill(''));
    } catch (error: unknown) {
      setError(getErrorMessage(error, 'Failed to resend OTP'));
    } finally {
      setResendLoading(false);
    }
  };

  const handleChange = (value: string, index: number) => {
    if (!/^\d?$/.test(value)) return; // Allow only a single digit

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setInputError(false);

    // Automatically focus the next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-input-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    index: number,
  ) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`otp-input-${index - 1}`);
      prevInput?.focus();
    }
  };

  const isOtpComplete = otp.every((digit) => digit !== '');

  const handleFormSubmit = async () => {
    try {
      setVerifyLoading(true);
      const response = await ApiService.put('v1/user/staff/login', {
        username: username,
        otp: otp.join(''),
        captchaToken: recaptchaVal,
      });
      const payload = response.data;
      if (!payload?.access_token) {
        setError('Invalid verification response. Please try again.');
        return;
      }

      const { access_token, refresh_token, member_details } = payload;

      dispatch(setCardDetails(member_details));

      setCookie('loungeId', member_details?.lounge_id, {
        path: '/',
        secure: true,
        sameSite: 'strict',
      });
      setCookie('loungeDetails', JSON.stringify(member_details?.lounge), {
        path: '/',
        secure: true,
        sameSite: 'strict',
      });
      setCookie('authToken', access_token, {
        path: '/',
        secure: true,
        sameSite: 'strict',
      });
      setCookie('refreshToken', refresh_token, {
        path: '/',
        secure: true,
        sameSite: 'strict',
      });
      setCookie('role', member_details?.role, {
        path: '/',
        secure: true,
        sameSite: 'strict',
      });

      // Use merchants from verify response first; fallback to merchant API
      const { merchants, error } = await getMerchantsWithLoginFallback(
        member_details?.merchants as Merchant[] | undefined,
      );

      if (!merchants.length) {
        if (error) {
          const status = getErrorStatus(error);
          if (status === 403) {
            toast.error(
              'Access Denied: You do not have permission to access any merchant. Please contact your administrator.',
              { autoClose: 5000 },
            );
          } else if (status === 401) {
            toast.error('Authentication failed. Please log in again.');
          } else {
            toast.error(
              getErrorMessage(
                error,
                'Failed to load merchants after multiple attempts.',
              ),
              { autoClose: 5000 },
            );
          }
        } else {
          toast.error(
            'No active merchants found for your account. Please contact support.',
            { autoClose: 5000 },
          );
        }
        setVerifyLoading(false);
        handleLogout();
        return;
      }

      dispatch(setMerchants(merchants));
      dispatch(setSelectedMerchant(merchants[0]));

      handleLogin();
      navigate('/', { state: { ...payload } });
    } catch (error: unknown) {
      setInputError(true);
      const msg = getErrorMessage(error);
      setError(msg);
      toast.error(msg, { autoClose: 5000 });
    } finally {
      setVerifyLoading(false);
    }
  };
  const handleCaptchaVerification = (value: string | null) => {
    if (value) {
      setRecaptchaVal(value);
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="text-center text-2xl font-semibold leading-[45px] text-[#1E429F] xl:text-3xl">
        Enter the 6 digit verification code
      </div>
      <div className="flex items-center justify-center gap-[16px] rounded-3xl bg-white px-12 py-10 shadow-md 2xl:py-[63px]">
        <div className="space-y-6 md:w-[506px]">
          <div className="flex flex-col items-center">
            <div className="mb-6 flex gap-4">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  id={`otp-input-${index}`}
                  type="text"
                  value={digit}
                  placeholder="-"
                  maxLength={1}
                  onChange={(e) => handleChange(e.target.value, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  disabled={verifyLoading || resendLoading}
                  className={`h-14 w-[71px] rounded-lg border text-center text-xl focus:outline-none focus:ring-2 ${
                    inputError
                      ? 'border-red-500 bg-red-50 focus:ring-red-500'
                      : 'border-[#D1D5DB] bg-gray-50 focus:ring-blue-500'
                  } ${
                    (verifyLoading || resendLoading) &&
                    'cursor-not-allowed opacity-50'
                  }`}
                />
              ))}
            </div>
            {error && <p className="mb-4 font-medium text-red-500">{error}</p>}
            <div className="flex w-full flex-col items-center gap-4">
              <Button
                disabled={
                  !isOtpComplete ||
                  verifyLoading ||
                  resendLoading ||
                  !recaptchaVal
                }
                buttonType="submit"
                text={verifyLoading ? 'Verifying...' : 'Verify'}
                className="w-full !text-lg"
                onClick={handleFormSubmit}
                loading={verifyLoading}
              />
              <div className="flex flex-col items-center gap-4">
                <ReCAPTCHA
                  sitekey={RECAPTCHA_SITE_KEY || ''}
                  onChange={handleCaptchaVerification}
                />
                <p className="text-sm text-gray-500">
                  Please complete the CAPTCHA verification to continue
                </p>
              </div>
              <div className="flex w-full flex-col items-center gap-2">
                {isResendDisabled && (
                  <p className="text-sm text-gray-400">
                    Resend OTP in: {countdown}s
                  </p>
                )}
                {!isResendDisabled && (
                  <Button
                    buttonType="submit"
                    text={resendLoading ? 'Sending...' : 'Resend OTP'}
                    className="!bg-red-200 !text-sm text-[#E1EFFE] hover:!bg-gray-50"
                    onClick={handleResendOtp}
                    loading={resendLoading}
                    disabled={verifyLoading || resendLoading}
                  />
                )}
              </div>
            </div>
            <button
              onClick={onBackToLogin}
              className={`mt-8 flex justify-center text-center text-sm text-[#1C64F2] underline xl:text-base ${
                verifyLoading || resendLoading
                  ? 'cursor-not-allowed opacity-50'
                  : 'cursor-pointer'
              }`}
              disabled={verifyLoading || resendLoading}
            >
              <div className="flex gap-2">
                <div>Back to Login</div>
                <svg
                  width="21"
                  height="21"
                  viewBox="0 0 21 21"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M9.77089 0.23999C12.2169 0.23999 14.2069 2.22999 14.2069 4.67599V5.60799C14.2069 6.02199 13.8709 6.35799 13.4569 6.35799C13.0429 6.35799 12.7069 6.02199 12.7069 5.60799V4.67599C12.7069 3.05599 11.3899 1.73999 9.77089 1.73999H4.89589C3.27889 1.73999 1.96289 3.05599 1.96289 4.67599V15.805C1.96289 17.424 3.27889 18.74 4.89589 18.74H9.78189C11.3939 18.74 12.7069 17.428 12.7069 15.816V14.873C12.7069 14.459 13.0429 14.123 13.4569 14.123C13.8709 14.123 14.2069 14.459 14.2069 14.873V15.816C14.2069 18.256 12.2209 20.24 9.78189 20.24H4.89589C2.45189 20.24 0.462891 18.251 0.462891 15.805V4.67599C0.462891 2.22999 2.45189 0.23999 4.89589 0.23999H9.77089ZM17.851 6.79399L20.779 9.70899C20.8051 9.73491 20.8285 9.76186 20.8499 9.79038L20.779 9.70899C20.8144 9.74392 20.8459 9.78209 20.8733 9.82279C20.8853 9.84124 20.8969 9.86031 20.9077 9.87991C20.9164 9.89522 20.9244 9.91121 20.9319 9.92745C20.9382 9.94181 20.9442 9.95617 20.9499 9.97076C20.9574 9.98977 20.964 10.0093 20.9698 10.0291C20.9742 10.0447 20.9782 10.0602 20.9817 10.076C20.9861 10.0951 20.9897 10.1143 20.9924 10.1337C20.994 10.1463 20.9955 10.1595 20.9967 10.1727C20.999 10.1956 21 10.2177 21 10.24L20.9949 10.302L20.9928 10.3417C20.9926 10.3434 20.9924 10.3451 20.9921 10.3468L21 10.24C21 10.2955 20.9938 10.3505 20.9819 10.4039C20.9782 10.4197 20.9742 10.4353 20.9697 10.4506C20.964 10.4707 20.9574 10.4902 20.95 10.5094C20.9442 10.5238 20.9382 10.5382 20.9317 10.5523C20.9244 10.5688 20.9164 10.5848 20.9078 10.6004C20.8969 10.6197 20.8853 10.6387 20.8729 10.6572C20.8659 10.6682 20.8582 10.679 20.8502 10.6896C20.8264 10.721 20.8004 10.7504 20.7723 10.7777L17.851 13.687C17.705 13.833 17.513 13.906 17.322 13.906C17.13 13.906 16.937 13.833 16.791 13.685C16.499 13.391 16.5 12.917 16.793 12.625L18.4329 10.99H8.20869C7.79469 10.99 7.45869 10.654 7.45869 10.24C7.45869 9.82599 7.79469 9.48999 8.20869 9.48999H18.4349L16.793 7.85599C16.5 7.56399 16.498 7.08999 16.791 6.79599C17.083 6.50199 17.557 6.50199 17.851 6.79399Z"
                    fill="#1C64F2"
                  />
                </svg>
              </div>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OtpInputPage;
